import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  TextField,
  Snackbar,
  Grid,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Button,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import useFirestore from "../../../../config/fireBaseHook"; // Import useFirestore
import ModalDialogTitle from "../../../common/ModalDialogTitle";
import ModalActions from "../../../common/ModalActions";
import ConfirmDeleteDialog from "../../../common/ConfirmDeleteDialog";

const Edit = ({
  open,
  onClose,
  ctag,
  onRecordUpdated,
  ptpStatusNotes,
  ptpStatusNotesLoading,
}) => {
  const { updateData: updateFirestoreData, deleteData: deleteFirestoreData } =
    useFirestore("CtagRetrievalForm"); // Use Firestore hook

  // Get status notes from CtagPermissionStatusNote collection
  const { data: statusNotesData, loading: statusNotesLoading } = useFirestore(
    "CtagPermissionStatusNote",
  );

  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [confirmCloseOpen, setConfirmCloseOpen] = useState(false);

  useEffect(() => {
    setFormData(ctag);
  }, [ctag]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleDateChange = (newValue) => {
    setFormData((prevData) => ({
      ...prevData,
      DOB: newValue,
    }));
  };

  const handleSubmit = async () => {
    setLoading(true);
    setSnackbarMessage("");
    try {
      const { id, ...dataToUpdate } = formData; // Separate id from the rest of the data
      if (!id) {
        throw new Error("Record ID is missing. Cannot update.");
      }
      await updateFirestoreData(id, dataToUpdate); // updateData from useFirestore handles updatedAt
      setSnackbarMessage("Record updated successfully!");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      onRecordUpdated(); // Call the callback function to refresh the list
    } catch (error) {
      console.error("Error updating record:", error);
      setSnackbarMessage(`Error updating record: ${error.message}`);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    setLoading(true);
    setSnackbarMessage("");
    try {
      if (!formData.id) {
        throw new Error("Record ID is missing. Cannot delete.");
      }
      await deleteFirestoreData(formData.id);
      setSnackbarMessage("Record deleted successfully!");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      onRecordUpdated(); // Call the callback function to refresh the list
      onClose(); // Close the dialog
    } catch (error) {
      console.error("Error deleting record:", error);
      setSnackbarMessage(`Error deleting record: ${error.message}`);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmDeleteOpen = () => {
    setConfirmDeleteOpen(true);
  };

  const handleConfirmDeleteClose = () => {
    setConfirmDeleteOpen(false);
  };

  const handleConfirmDelete = () => {
    handleConfirmDeleteClose();
    handleDelete();
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbarOpen(false);
  };

  // The X (and backdrop/Escape, which share the same Dialog onClose) always
  // asks for confirmation first rather than closing immediately, so an
  // in-progress edit isn't lost by an accidental click.
  const handleCloseAttempt = () => {
    setConfirmCloseOpen(true);
  };

  const handleCancelClose = () => {
    setConfirmCloseOpen(false);
  };

  const handleConfirmClose = () => {
    setConfirmCloseOpen(false);
    onClose();
  };

  return (
    <>
      <Dialog open={open} onClose={handleCloseAttempt} maxWidth="md" fullWidth>
        <ModalDialogTitle onClose={handleCloseAttempt}>
          Edit Record
        </ModalDialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth margin="normal">
                <InputLabel id="data-status-note-label">
                  PTP Status Note
                </InputLabel>
                <Select
                  labelId="data-status-note-label"
                  name="Data_Status_Note"
                  value={formData?.Data_Status_Note || ""}
                  onChange={handleChange}
                  label="PTP Status Note"
                  disabled={ptpStatusNotesLoading}
                >
                  {ptpStatusNotesLoading && (
                    <MenuItem disabled>Loading...</MenuItem>
                  )}
                  {!ptpStatusNotesLoading &&
                    (!ptpStatusNotes || ptpStatusNotes.length === 0) && (
                      <MenuItem disabled>No status notes available</MenuItem>
                    )}
                  {ptpStatusNotes &&
                    ptpStatusNotes
                      .map((statusNote) => statusNote.statusNote)
                      .filter(Boolean)
                      .sort((a, b) => a.localeCompare(b))
                      .map((statusNote) => (
                        <MenuItem key={statusNote} value={statusNote}>
                          {statusNote}
                        </MenuItem>
                      ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Last Name"
                name="Last_Name"
                value={formData?.Last_Name || ""}
                onChange={handleChange}
                fullWidth
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="First Name"
                name="First_Name"
                value={formData?.First_Name || ""}
                onChange={handleChange}
                fullWidth
                margin="normal"
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                label="Tartan ID"
                name="Tartan_Id"
                value={formData?.Tartan_Id || ""}
                onChange={handleChange}
                fullWidth
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Sinclair Email"
                name="Sinclair_Email"
                value={formData?.Sinclair_Email || ""}
                onChange={handleChange}
                fullWidth
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="HSGY"
                name="HSGY"
                value={formData?.HSGY || ""}
                onChange={handleChange}
                fullWidth
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Personal Email"
                name="Personal_Email"
                value={formData?.Personal_Email || ""}
                onChange={handleChange}
                fullWidth
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Ohio SSID"
                name="Ohio_SSID"
                value={formData?.Ohio_SSID || ""}
                onChange={handleChange}
                fullWidth
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Current Sinclair Student"
                name="Current_Sinclair_Student"
                value={formData?.Current_Sinclair_Student || ""}
                onChange={handleChange}
                fullWidth
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Earned CTAG"
                name="Earned_CTAG"
                value={formData?.Earned_CTAG || ""}
                onChange={handleChange}
                fullWidth
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="CTAG Site of Delivery"
                name="CTAG_Site_of_Delivery"
                value={formData?.CTAG_Site_of_Delivery || ""}
                onChange={handleChange}
                fullWidth
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Forms Sent From CT Institute"
                name="Forms_Sent_From_CT_Institute"
                value={formData?.Forms_Sent_From_CT_Institute || ""}
                onChange={handleChange}
                fullWidth
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="CTAG Instructor"
                name="CTAG_Instructor"
                value={formData?.CTAG_Instructor || ""}
                onChange={handleChange}
                fullWidth
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Phone Number"
                name="Phone_Number"
                value={formData?.Phone_Number || ""}
                onChange={handleChange}
                fullWidth
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="Date of Birth"
                  value={formData?.DOB ? dayjs(formData.DOB) : null}
                  onChange={handleDateChange}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      margin: "normal",
                    },
                  }}
                  maxDate={dayjs()}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Created At"
                name="created_at"
                value={(() => {
                  const raw = formData?.created_at || ctag?.created_at || "";
                  if (!raw) return "";

                  let date;
                  // Handle Firestore Timestamp
                  if (raw && typeof raw.toDate === "function") {
                    date = raw.toDate();
                  }
                  // Handle regular Date object
                  else if (raw instanceof Date) {
                    date = raw;
                  }
                  // Handle string or number
                  else {
                    date = new Date(raw);
                  }

                  // Format as mm-dd-yyyy if valid date
                  if (date && !isNaN(date.getTime())) {
                    const mm = String(date.getMonth() + 1).padStart(2, "0");
                    const dd = String(date.getDate()).padStart(2, "0");
                    const yyyy = date.getFullYear();
                    return `${mm}-${dd}-${yyyy}`;
                  }
                  return raw;
                })()}
                InputProps={{
                  readOnly: true,
                  style: { color: "#888", backgroundColor: "#f5f5f5" },
                }}
                fullWidth
                margin="normal"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <ModalActions
          onSave={handleSubmit}
          saving={loading}
          onDelete={handleConfirmDeleteOpen}
          deleting={loading}
        />
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={6000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <Alert
            onClose={handleSnackbarClose}
            severity={snackbarSeverity}
            sx={{ width: "100%" }}
            variant="filled"
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Dialog>
      <ConfirmDeleteDialog
        open={confirmDeleteOpen}
        onClose={handleConfirmDeleteClose}
        onConfirm={handleConfirmDelete}
        deleting={loading}
        message="Are you sure you want to delete this?"
      />
      <Dialog
        open={confirmCloseOpen}
        onClose={handleCancelClose}
        maxWidth="xs"
        fullWidth
      >
        <DialogContent sx={{ pt: 3 }}>
          <Typography>
            Are you sure you want to close before saving your changes?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCancelClose}>No</Button>
          <Button onClick={handleConfirmClose} variant="contained" color="error">
            Yes, Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default Edit;
